// SAB-431: cookie consent banner. Sticky bottom of viewport, ≤80px tall on
// mobile per ticket, doesn't push the hero below the fold. Three buttons —
// Reject all / Customize / Accept all — and that's it; technical detail
// belongs on the /cookies page (SAB-430).
//
// Brand voice direction (from Adam, locked 2026-06-19):
//   irreverent, blunt, not corporate; mirror Home.tsx + LandingPage.tsx;
//   no compliance theater in the visible banner.
import { useEffect, useRef, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Button } from '@/components/ui/button'
import {
  acceptAll,
  rejectAll,
  subscribeConsent,
  needsBanner,
} from '@/lib/consent'
import CustomizeModal from './CustomizeModal'

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false)
  const [showCustomize, setShowCustomize] = useState(false)
  const bannerRef = useRef<HTMLDivElement | null>(null)

  // While the fixed banner is showing, pad the page bottom by exactly the
  // banner's rendered height so nothing (footer legal bar, last content row)
  // is obscured for a first-time visitor. ResizeObserver keeps the padding
  // honest across the taller two-row mobile layout, viewport resizes, and the
  // safe-area inset (all part of the measured height). Cleared on dismiss and
  // unmount so the page collapses back cleanly.
  //
  // SAB-916: the same measurement is published as --consent-banner-h, which the
  // Freshdesk launcher's offset in index.html reads. This banner is z-[60] and the
  // launcher is z-55, so before this the banner sat ON TOP of the launcher and ate
  // its clicks: measured on the real app, 69 of 75px covered at 1280, and the FULL
  // 75px at both 390 and 430 -- the launcher was untappable on a phone for every
  // pre-consent visitor, on every route. The launcher moves rather than the banner:
  // raising it above z-[60] would park it over these buttons instead, and consent
  // controls losing a fight with a support launcher is the worse of the two bugs.
  //
  // It is published rather than hardcoded because the height genuinely varies with
  // the wrapping layout -- 89px at 1280, 152px at 430, 172px at 390 -- so any CSS
  // constant would be wrong at some width. The ResizeObserver already running for
  // the padding keeps both honest for free.
  useEffect(() => {
    const el = bannerRef.current
    const clear = () => {
      document.body.style.paddingBottom = ''
      document.documentElement.style.removeProperty('--consent-banner-h')
    }
    if (!visible || !el) {
      clear()
      return
    }
    const apply = () => {
      const h = el.getBoundingClientRect().height
      document.body.style.paddingBottom = `${h}px`
      document.documentElement.style.setProperty('--consent-banner-h', `${h}px`)
    }
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(el)
    return () => {
      ro.disconnect()
      clear()
    }
  }, [visible])

  useEffect(() => {
    // SAB-706: never surface the web cookie-consent banner on native. Cookie
    // consent is the web / ePrivacy mechanism; on native, Apple's frameworks
    // govern and a cookie prompt inside the WKWebView triggers guideline
    // 5.1.2(i). Native defaults analytics ON (see lib/consent NATIVE_DEFAULT) and
    // per-category control stays available in Settings (CustomizeModal).
    if (Capacitor.isNativePlatform()) return
    // Initial mount: check current state. Then subscribe so resetConsent()
    // (from the Footer link) re-renders us into view.
    setVisible(needsBanner())
    const unsub = subscribeConsent(() => setVisible(needsBanner()))
    return unsub
  }, [])

  if (Capacitor.isNativePlatform() || !visible) return null

  return (
    <>
      <div
        ref={bannerRef}
        role="region"
        aria-label="Cookie consent"
        className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/95 backdrop-blur-xl shadow-[0_-4px_24px_rgba(0,0,0,0.4)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        <div className="mx-auto max-w-6xl px-4 py-3 sm:py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-sm sm:text-base text-foreground">
              Cookies. Yeah, we use them.
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground leading-snug mt-0.5">
              Some keep you logged in. Others help us see who's actually playing. Your call which ones we run.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0 sm:flex-nowrap">
            <Button
              variant="ghost"
              size="sm"
              onClick={rejectAll}
              className="text-xs h-8"
            >
              Reject all
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCustomize(true)}
              className="text-xs h-8"
            >
              Customize
            </Button>
            <Button
              size="sm"
              onClick={acceptAll}
              className="text-xs h-8 bg-primary hover:bg-primary/90"
            >
              Accept all
            </Button>
          </div>
        </div>
      </div>

      <CustomizeModal
        open={showCustomize}
        onClose={() => setShowCustomize(false)}
      />
    </>
  )
}
